import axios from 'axios';

import { parameters, getLocale } from '../../localization/strings';
import { FullAnswer } from '../../entities/full-answer';
import { AllergensAnswer } from '../../entities/allergens-answer';
import { StatusResponse } from '../../entities/status-response';

export const getResult = async (token: string, hash: string, langUrl?: string): Promise<FullAnswer> => {
  const url = `${parameters().api.baseUrl}/orders/result/${hash}`;

  const result = await axios.get(url, {
    params: {
      language: langUrl || getLocale(),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return result.data;
};

export const getAllergens = async (token: string, hash: string, langUrl?: string): Promise<AllergensAnswer> => {
  const url = `${parameters().api.baseUrl}/orders/allergens/${hash}`;

  const result = await axios.get(url, {
    params: {
      language: langUrl || getLocale(),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return result.data;
};

export const storeBloodLocation = async (
  token: string,
  orderHash: string,
  bloodLocationId: number,
): Promise<StatusResponse> => {
  const url = `${parameters().api.baseUrl}/orders/store-location`;

  const result = await axios.post(
    url,
    {
      orderHash,
      bloodLocationId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return result.data;
};

export const generateResultPdf = async (token: string, orderHash: string, language: string): Promise<string> => {
  const url = `${parameters().api.baseUrl}/orders/get-result-pdf/${orderHash}`;

  const result = await axios.get(url, {
    params: { language },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return result.data;
};

export const getPdf = async (token: string, pdfId: string): Promise<string> => {
  const url = `${parameters().api.baseUrl}/orders/download-pdf/${pdfId}`;

  const result = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return result.data;
};

export const finishSurvey = async (token: string, hash: string): Promise<string> => {
  const url = `${parameters().api.baseUrl}/orders/finish-survey/${hash}`;

  const result = await axios.post(
    url,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return result.data;
};

export interface ShopifyOrderDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  hasAccount: boolean;
  region: 'eu' | 'uk' | 'us';
  locale: string;
  answerHash?: string;
  productId: number;
  extraSurveyId?: number;
}

export const getOrderByShopifyId = async (shopifyOrderId: string, email: string): Promise<{ orderHash: string }> => {
  const url = `${parameters().api.baseUrl}/orders/shopify/${shopifyOrderId}`;

  const result = await axios.get(url, {
    params: { email },
  });

  return result.data;
};

export const getShopifyDetails = async (orderHash: string): Promise<ShopifyOrderDetails> => {
  const url = `${parameters().api.baseUrl}/orders/shopify-details/${orderHash}`;

  const result = await axios.get(url);

  return result.data;
};

export const linkOrderToCustomer = async (
  token: string,
  orderHash: string,
): Promise<{ status: string; orderStatus: string }> => {
  const url = `${parameters().api.baseUrl}/orders/link-to-customer`;

  const result = await axios.post(
    url,
    { orderHash },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return result.data;
};

export const assignPatientToShopifyOrders = async (
  token: string,
  shopifyOrderId: string,
  patientId: number,
): Promise<{ status: string; ordersUpdated: number; patientId: number }> => {
  const url = `${parameters().api.baseUrl}/orders/assign-patient-shopify`;

  const result = await axios.post(
    url,
    { shopifyOrderId, patientId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return result.data;
};

export const assignPatientToOrder = async (
  token: string,
  orderHash: string,
  patientId: number,
): Promise<{ status: string; patientId: number; questionnaireReset: boolean }> => {
  const url = `${parameters().api.baseUrl}/orders/${orderHash}/patient`;

  const result = await axios.post(
    url,
    { patientId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return result.data;
};
